import React, { useState, useEffect, useCallback } from 'react';
import { ArrowLeft, Eye, Calendar, Layers, Clock, X, Download, Edit } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import './CreativeHistory.css';
import Sidebar from './Sidebar';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const CreativeHistory = () => {
  const [creatives, setCreatives] = useState([]);
  const [improvements, setImprovements] = useState([]);
  const [activeSection, setActiveSection] = useState('campaigns'); // 'campaigns' or 'improvements'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days] = useState(7);
  const [selectedCreativeId, setSelectedCreativeId] = useState(null);
  const [selectedImprovementId, setSelectedImprovementId] = useState(null);
  const navigate = useNavigate();
  const params = useParams();

  const fetchCreativeHistory = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/creative-history?days=${days}`);
      const data = await response.json();

      if (data.success) {
        setCreatives(data.history);
      } else {
        setError('Failed to load creative history');
      }
    } catch (err) {
      setError('Error fetching creative history: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchImprovements = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/standalone-improvements/history?days=${days}`);
      const data = await response.json();

      if (data.success) {
        setImprovements(data.improvements);
      }
    } catch (err) {
      console.error('Error fetching improvements:', err);
    }
  }, [days]);

  useEffect(() => {
    fetchCreativeHistory();
    fetchImprovements();
  }, [fetchCreativeHistory, fetchImprovements]);

  // Check if there's a creative ID in the URL and open modal
  useEffect(() => {
    if (params.id) {
      setSelectedCreativeId(params.id);
    } else {
      setSelectedCreativeId(null);
    }
  }, [params.id]);

  const handleViewCreative = (creativeId) => {
    // Update URL without full navigation
    window.history.pushState({}, '', `/creative-history/${creativeId}`);
    setSelectedCreativeId(creativeId);
  };

  const handleViewImprovement = (improvementId) => {
    setSelectedImprovementId(improvementId);
  };

  const handleCloseModal = () => {
    // Go back to history list URL
    window.history.pushState({}, '', '/creative-history');
    setSelectedCreativeId(null);
  };

  const handleCloseImprovementModal = () => {
    setSelectedImprovementId(null);
  };

  if (loading) {
    return (
      <div className="creative-history-loading">
        <div className="spinner"></div>
        <p>Loading creative history...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="creative-history-error">
        <p>{error}</p>
        <button onClick={fetchCreativeHistory}>Retry</button>
      </div>
    );
  }

  return (
    <div className="creative-brief-container">

      <div className="flex relative">
              
          <Sidebar/>
  
          <main className="bg-white flex-1 px-3 pb-3">
            {/* Header */}
            <div className="creative-history-header">
              <button className="back-button" onClick={() => navigate('/')}>
                <ArrowLeft size={20} />
              </button>
              <div className="header-content">
                <h1 className="page-title">Creative History</h1>
                <p className="header-subtitle">View all creatives generated in the last {days} days</p>
              </div>
            </div>
            <div className='main-layout p-5 py-10'>
              <div className="w-full lg:w-[80%] mx-auto">
                {/* Section Tabs */}
                <div className="history-tabs">
                  <button
                    className={`tab ${activeSection === 'campaigns' ? 'active' : ''}`}
                    onClick={() => setActiveSection('campaigns')}
                  >
                    Ad History ({creatives.length})
                  </button>
                  <button
                    className={`tab ${activeSection === 'improvements' ? 'active' : ''}`}
                    onClick={() => setActiveSection('improvements')}
                  >
                    Improvements ({improvements.length})
                  </button>
                </div>

                {/* Ad History Section */}
                {activeSection === 'campaigns' && (
                  <div className="creative-list">
                    {creatives.length === 0 ? (
                      <div className="empty-state">
                        <p>No creatives generated in the last {days} days</p>
                        <button onClick={() => navigate('/')}>
                          Generate Your First Creative
                        </button>
                      </div>
                    ) : (
                      creatives.map((creative) => (
                        <CreativeCard
                          key={creative.id}
                          creative={creative}
                          onView={handleViewCreative}
                        />
                      ))
                    )}
                  </div>
                )}

                {/* Improvements Section */}
                {activeSection === 'improvements' && (
                  <div className="improvements-list">
                    {improvements.length === 0 ? (
                      <div className="empty-state">
                        <p>No improvements in the last {days} days</p>
                        <button onClick={() => navigate('/improvements')}>
                          Upload & Improve Image
                        </button>
                      </div>
                    ) : (
                      improvements.map((improvement) => (
                        <ImprovementCard
                          key={improvement.id}
                          improvement={improvement}
                          onView={handleViewImprovement}
                        />
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Creative Detail Modal */}
            {selectedCreativeId && (
              <CreativeDetailModal
                creativeId={selectedCreativeId}
                onClose={handleCloseModal}
              />
            )}

            {/* Improvement Detail Modal */}
            {selectedImprovementId && (
              <ImprovementDetailModal
                improvementId={selectedImprovementId}
                onClose={handleCloseImprovementModal}
              />
            )}

          </main>
      </div>
    </div>
  );
};

const CreativeCard = ({ creative, onView }) => {
  return (
    <div className="creative-card">
      <div className="creative-card-content">
        {/* Left Section - Brand Info */}
        <div className="creative-info">
          {/* Brand Name & Variant Badge */}
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-semibold capitalize">{creative.brandName}</h3>
            <div className="variant-badge">{creative.variantCount} variants</div>
          </div>
          {/* <div className="creative-header">
            <h3 className="brand-name">{creative.brandName}</h3>
            <span className="variant-badge">{creative.variantCount} variants</span>
          </div> */}

          {/* Metadata Row */}
          <div className="creative-meta">
            <div className="meta-item">
              <Clock size={16} />
              <span>{creative.relativeTime}</span>
            </div>
            <div className="meta-item">
              <Calendar size={16} />
              <span>{formatDateTime(creative.timestamp)}</span>
            </div>
            <div className="meta-item">
              <Layers size={16} />
              <span>{creative.formatCount} formats</span>
            </div>
          </div>

          {/* Tags */}
          <div className="creative-tags">
            {creative.tags && creative.tags.filter(tag => tag).map((tag, index) => (
              <span key={index} className="tag">
                {tag}
              </span>
            ))}
          </div>
        </div>

        {/* Right Section - Thumbnails & View Button */}
        <div className="creative-actions">
          {/* Thumbnail Preview */}
          <div className="thumbnail-preview">
            {creative.thumbnailUrl && (
              <img
                src={creative.thumbnailUrl}
                alt={creative.brandName}
                className="thumbnail-image"
              />
            )}
          </div>

          {/* View Button */}
          <button
            className="view-button"
            onClick={() => onView(creative.id)}
          >
            <Eye size={18} />
            <span>View</span>
          </button>
        </div>
      </div>
    </div>
  );
};

const ImprovementCard = ({ improvement, onView }) => {
  return (
    <div className="improvement-card">
      <div className="improvement-card-content">
        {/* Left Section - Info */}
        <div className="improvement-info">
          <div className="flex items-center gap-3 mb-2">
            {improvement.brandName && (
              <h3 className="text-lg font-semibold capitalize">{improvement.brandName}</h3>
            )}
            <div className="standalone-badge">Standalone</div>
            {improvement.hasVariants && (
              <div className="variant-count-badge">
                +{improvement.variantCount} {improvement.variantCount === 1 ? 'variant' : 'variants'}
              </div>
            )}
          </div>

          {/* Metadata Row */}
          <div className="creative-meta">
            <div className="meta-item">
              <Clock size={16} />
              <span>{improvement.relativeTime}</span>
            </div>
            {improvement.textChanges && improvement.textChanges.length > 0 && (
              <div className="meta-item">
                <Layers size={16} />
                <span>{improvement.textChanges.length} text changes</span>
              </div>
            )}
          </div>

          {/* Notes */}
          {improvement.notes && (
            <div className="improvement-notes">
              <p className="text-sm text-gray-600">{improvement.notes}</p>
            </div>
          )}
        </div>

        {/* Right Section - Before/After Preview & View Button */}
        <div className="creative-actions">
          {/* Before/After Preview */}
          <div className="before-after-thumbnails">
            <div className="thumbnail-wrapper">
              <span className="thumbnail-label">Original</span>
              <img
                src={improvement.originalImageUrl}
                alt="Original"
                className="thumbnail-image"
              />
            </div>
            <div className="arrow-separator">→</div>
            <div className="thumbnail-wrapper">
              <span className="thumbnail-label">Improved</span>
              <img
                src={improvement.improvedImageUrl}
                alt="Improved"
                className="thumbnail-image"
              />
            </div>
          </div>

          {/* View Button */}
          <button
            className="view-button"
            onClick={() => onView(improvement.id)}
          >
            <Eye size={18} />
            <span>View</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// Improvement Detail Modal Component
const ImprovementDetailModal = ({ improvementId, onClose }) => {
  const [improvement, setImprovement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Variant management
  const [allVariants, setAllVariants] = useState([]);
  const [currentVariantIndex, setCurrentVariantIndex] = useState(0);

  // Edit/Improvement state
  const [showImprovementModal, setShowImprovementModal] = useState(false);
  const [selectedImageForEdit, setSelectedImageForEdit] = useState(null);
  const [editingImageType, setEditingImageType] = useState(null); // 'original' or 'improved'
  const [analyzingText, setAnalyzingText] = useState(false);
  const [textMetadata, setTextMetadata] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [textEdits, setTextEdits] = useState({});
  const [freeformInstructions, setFreeformInstructions] = useState('');
  const [improvementLoading, setImprovementLoading] = useState(false);
  const [improvementError, setImprovementError] = useState(null);
  const [improvementSuccess, setImprovementSuccess] = useState(false);

  // Handler to open image in new tab
  const handleViewImage = (imageUrl) => {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  // Handler to download cross-origin images using backend proxy
  const handleDownloadImage = (imageUrl, filename) => {
    try {
      // Use backend proxy endpoint to download the image
      const downloadUrl = `${API_BASE_URL}/download-image?url=${encodeURIComponent(imageUrl)}&filename=${encodeURIComponent(filename || 'image.jpg')}`;

      // Create a temporary link and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename || 'image.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  // Handler: Switch between variants
  const handleVariantChange = (e) => {
    const selectedIndex = parseInt(e.target.value);
    setCurrentVariantIndex(selectedIndex);
    // Update the displayed improvement to the selected variant
    setImprovement(allVariants[selectedIndex]);
  };

  // Handler: Edit image (analyze and open nested modal)
  const handleEditImage = async (imageUrl, imageType) => {
    setSelectedImageForEdit(imageUrl);
    setEditingImageType(imageType);
    setAnalyzingText(true);
    setAnalysisError(null);
    setTextMetadata(null);
    setShowImprovementModal(true);

    try {
      const response = await fetch(`${API_BASE_URL}/standalone-improvements/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: imageUrl })
      });

      const data = await response.json();

      if (data.success) {
        setTextMetadata({
          textElements: data.data?.textElements || data.textElements || [],
          tokenUsage: data.data?.tokenUsage || data.tokenUsage || {}
        });
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (error) {
      setAnalysisError(error.message);
      // Still allow freeform improvements
      setTextMetadata({ textElements: [], tokenUsage: {} });
    } finally {
      setAnalyzingText(false);
    }
  };

  // Handler: Apply improvements and generate new variant
  const handleApplyEdit = async () => {
    setImprovementLoading(true);
    setImprovementError(null);

    try {
      // Build text changes array from edits
      const textChanges = [];
      if (textMetadata && textMetadata.textElements) {
        textMetadata.textElements.forEach((element, textIndex) => {
          const newText = textEdits[textIndex];
          if (newText !== undefined && newText !== element.text) {
            textChanges.push({
              originalText: element.text,
              newText: newText
            });
          }
        });
      }

      // Get original image metadata
      const originalMetadata = {
        width: improvement.dimensions?.original?.width || improvement.dimensions?.improved?.width,
        height: improvement.dimensions?.original?.height || improvement.dimensions?.improved?.height,
        gcsPath: editingImageType === 'original'
          ? improvement.originalImageUrl.split('.com/')[1]
          : improvement.improvedImageUrl.split('.com/')[1]
      };

      // Determine parent ID (if current is a variant, use its parent, otherwise use current ID)
      const parentId = improvement.parentImprovementId || improvement.id;

      const payload = {
        originalImageUrl: selectedImageForEdit,
        originalMetadata: originalMetadata,
        textChanges: textChanges,
        freeformInstructions: freeformInstructions,
        detectedTextElements: textMetadata?.textElements || [],
        analysisTokenUsage: textMetadata?.tokenUsage || {},
        brandName: improvement.brandName || '',
        notes: `Re-improved from ${editingImageType} image of improvement ${improvement.id}`,
        parentImprovementId: parentId
      };

      const response = await fetch(`${API_BASE_URL}/standalone-improvements/improve`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setImprovementSuccess(true);
        // Refresh to show new variant
        setTimeout(async () => {
          // Fetch updated variants list
          const variantsResponse = await fetch(`${API_BASE_URL}/standalone-improvements/${improvementId}/variants`);
          const variantsData = await variantsResponse.json();

          if (variantsData.success) {
            setAllVariants(variantsData.variants);
            // Switch to the newly created variant (last one in array)
            const newVariantIndex = variantsData.variants.length - 1;
            setCurrentVariantIndex(newVariantIndex);
            setImprovement(variantsData.variants[newVariantIndex]);
          }

          handleCloseImprovementModal();
        }, 2000);
      } else {
        throw new Error(data.error || 'Improvement failed');
      }
    } catch (error) {
      setImprovementError(error.message);
    } finally {
      setImprovementLoading(false);
    }
  };

  // Handler: Close nested improvement modal
  const handleCloseImprovementModal = () => {
    setShowImprovementModal(false);
    setSelectedImageForEdit(null);
    setEditingImageType(null);
    setTextMetadata(null);
    setAnalysisError(null);
    setTextEdits({});
    setFreeformInstructions('');
    setImprovementError(null);
    setImprovementSuccess(false);
  };

  const fetchImprovementDetails = useCallback(async () => {
    try {
      setLoading(true);

      // Fetch the specific improvement
      const response = await fetch(`${API_BASE_URL}/standalone-improvements/${improvementId}`);
      const data = await response.json();

      if (data.success) {
        setImprovement(data.improvement);

        // Also fetch all variants (parent + children)
        try {
          const variantsResponse = await fetch(`${API_BASE_URL}/standalone-improvements/${improvementId}/variants`);
          const variantsData = await variantsResponse.json();

          if (variantsData.success && variantsData.variants) {
            setAllVariants(variantsData.variants);
            // Find current variant index
            const currentIndex = variantsData.variants.findIndex(v => v.id === improvementId);
            if (currentIndex !== -1) {
              // User clicked on a specific improvement, show that one
              setCurrentVariantIndex(currentIndex);
            } else {
              // Default to latest variant (last in array)
              const latestIndex = variantsData.variants.length - 1;
              setCurrentVariantIndex(latestIndex);
              setImprovement(variantsData.variants[latestIndex]);
            }
          } else {
            // If variants fetch fails, just show the single improvement
            setAllVariants([]);
          }
        } catch (variantsErr) {
          console.error('Error fetching variants:', variantsErr);
          setAllVariants([]);
        }
      } else {
        setError('Failed to load improvement details');
      }
    } catch (err) {
      setError('Error fetching improvement details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [improvementId]);

  useEffect(() => {
    fetchImprovementDetails();
  }, [fetchImprovementDetails]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showImprovementModal) {
          handleCloseImprovementModal();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, showImprovementModal]);

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading improvement details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!improvement) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content improvement-detail-modal" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header */}
        <div className="modal-header">
          <div className="modal-header-info">
            <h2>{improvement.brandName || 'Improvement Details'}</h2>
            <p className="modal-subtitle">
              {improvement.relativeTime || formatDateTime(improvement.createdAt)}
            </p>
          </div>

          {/* Variant selector dropdown */}
          {allVariants && allVariants.length > 1 && (
            <div className="variant-selector">
              <label htmlFor="variant-select">Variant:</label>
              <select
                id="variant-select"
                value={currentVariantIndex}
                onChange={handleVariantChange}
                className="variant-dropdown"
              >
                {allVariants.map((variant, index) => (
                  <option key={variant.id} value={index}>
                    {!variant.parentImprovementId ? 'Original' : `Variant ${index}`} - {variant.relativeTime}
                  </option>
                ))}
              </select>
            </div>
          )}

          <button className="modal-close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Before/After Comparison */}
          <div className="before-after-comparison">
            <div className="comparison-section">
              <h3>Original Image</h3>
              <div className="comparison-image-wrapper">
                <img
                  src={improvement.originalImageUrl}
                  alt="Original"
                  className="comparison-image"
                />
                <div className="modal-image-actions">
                  <button
                    onClick={() => handleEditImage(improvement.originalImageUrl, 'original')}
                    className="action-button"
                    title="Edit/Improve"
                    disabled={analyzingText || improvementLoading}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleViewImage(improvement.originalImageUrl)}
                    className="action-button"
                    title="View in new tab"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => handleDownloadImage(improvement.originalImageUrl, `original-${improvement.id}.jpg`)}
                    className="action-button"
                    title="Download"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              {improvement.dimensions && improvement.dimensions.original && (
                <div className="image-info">
                  <span>{improvement.dimensions.original.width} × {improvement.dimensions.original.height}</span>
                  {improvement.dimensions.original.size && (
                    <span>{(improvement.dimensions.original.size / 1024).toFixed(2)} KB</span>
                  )}
                </div>
              )}
            </div>

            <div className="comparison-section">
              <h3>Improved Image</h3>
              <div className="comparison-image-wrapper">
                <img
                  src={improvement.improvedImageUrl}
                  alt="Improved"
                  className="comparison-image"
                />
                <div className="modal-image-actions">
                  <button
                    onClick={() => handleEditImage(improvement.improvedImageUrl, 'improved')}
                    className="action-button"
                    title="Edit/Improve"
                    disabled={analyzingText || improvementLoading}
                  >
                    <Edit size={16} />
                  </button>
                  <button
                    onClick={() => handleViewImage(improvement.improvedImageUrl)}
                    className="action-button"
                    title="View in new tab"
                  >
                    <Eye size={16} />
                  </button>
                  <button
                    onClick={() => handleDownloadImage(improvement.improvedImageUrl, `improved-${improvement.id}.jpg`)}
                    className="action-button"
                    title="Download"
                  >
                    <Download size={16} />
                  </button>
                </div>
              </div>
              {improvement.dimensions && improvement.dimensions.improved && (
                <div className="image-info">
                  <span>{improvement.dimensions.improved.width} × {improvement.dimensions.improved.height}</span>
                  {improvement.dimensions.improved.size && (
                    <span>{(improvement.dimensions.improved.size / 1024).toFixed(2)} KB</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Improvement Details Card */}
          {(improvement.textChanges?.length > 0 || improvement.freeformInstructions || improvement.notes) && (
            <div className="improvement-details-card">
              <h3>Modification Details</h3>

              {/* Text Changes */}
              {improvement.textChanges && improvement.textChanges.length > 0 && (
                <div className="details-section">
                  <h4>Text Changes:</h4>
                  {improvement.textChanges.map((change, index) => (
                    <div key={index} className="text-change-item">
                      <div className="change-label">Original:</div>
                      <div className="change-value">{change.originalText}</div>
                      <div className="change-label">Changed to:</div>
                      <div className="change-value changed">{change.newText}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Freeform Instructions */}
              {improvement.freeformInstructions && (
                <div className="details-section">
                  <h4>Additional Instructions:</h4>
                  <p className="instruction-text">{improvement.freeformInstructions}</p>
                </div>
              )}

              {/* Notes */}
              {improvement.notes && (
                <div className="details-section">
                  <h4>Notes:</h4>
                  <p className="note-text">{improvement.notes}</p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Nested Improvement Modal */}
        {showImprovementModal && (
          <div className="modal-overlay nested-improvement-modal" onClick={handleCloseImprovementModal}>
            <div className="improvement-modal" onClick={(e) => e.stopPropagation()}>
              <div className="improvement-modal-header">
                <h3>Edit & Improve Image</h3>
                <button onClick={handleCloseImprovementModal} className="modal-close-btn" disabled={improvementLoading}>×</button>
              </div>

              <div className="improvement-modal-body">
                {/* Original Image Preview */}
                {selectedImageForEdit && (
                  <div className="original-creative-preview">
                    <h4>Editing {editingImageType === 'original' ? 'Original' : 'Improved'} Image:</h4>
                    <img src={selectedImageForEdit} alt={editingImageType} />
                  </div>
                )}

                {/* Analysis Loading */}
                {analyzingText && (
                  <div className="analyzing-state">
                    <div className="spinner-small"></div>
                    <p>Analyzing image for text...</p>
                  </div>
                )}

                {/* Analysis Error */}
                {analysisError && (
                  <div className="analysis-error">
                    {analysisError}
                    <p className="error-hint">You can still use freeform instructions below.</p>
                  </div>
                )}

                {/* Text Editors */}
                {!analyzingText && textMetadata && textMetadata.textElements && textMetadata.textElements.length > 0 && (
                  <div className="text-editors-section">
                    <h4>Edit Text Elements:</h4>
                    {textMetadata.textElements.map((element, index) => (
                      <div key={index} className="text-editor-row">
                        <div className="text-editor-labels">
                          <span className="text-position-badge">{element.position}</span>
                          <span className="text-type-badge">{element.type}</span>
                          <span className="text-size-badge">{element.size}</span>
                        </div>
                        <input
                          type="text"
                          value={textEdits[index] !== undefined ? textEdits[index] : element.text}
                          onChange={(e) => setTextEdits(prev => ({ ...prev, [index]: e.target.value }))}
                          placeholder={element.text}
                          className="text-editor-input"
                          disabled={improvementLoading}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* No text found */}
                {!analyzingText && textMetadata && textMetadata.textElements && textMetadata.textElements.length === 0 && (
                  <div className="no-text-found">
                    No text detected automatically. Use freeform instructions below to describe your changes.
                  </div>
                )}

                {/* Freeform Instructions */}
                {!analyzingText && textMetadata && (
                  <div className="freeform-section">
                    <h4>Additional Instructions (Optional):</h4>
                    <textarea
                      value={freeformInstructions}
                      onChange={(e) => setFreeformInstructions(e.target.value)}
                      placeholder="e.g., change background to blue, make logo bigger, remove watermark..."
                      className="freeform-textarea"
                      rows="4"
                      disabled={improvementLoading}
                    />
                    <p className="freeform-hint">Describe any changes: remove elements, change colors, adjust positioning, etc.</p>
                  </div>
                )}

                {/* Error */}
                {improvementError && (
                  <div className="improvement-error">{improvementError}</div>
                )}

                {/* Success */}
                {improvementSuccess && (
                  <div className="improvement-success">
                    Improvement complete! Refreshing...
                  </div>
                )}
              </div>

              <div className="improvement-modal-footer">
                <button onClick={handleCloseImprovementModal} className="btn-secondary" disabled={improvementLoading}>
                  Cancel
                </button>
                <button onClick={handleApplyEdit} className="btn-primary" disabled={improvementLoading || improvementSuccess || analyzingText}>
                  {improvementLoading ? (
                    <>
                      <span className="spinner-small"></span>
                      Improving...
                    </>
                  ) : (
                    'Apply Improvements'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Helper function to format date/time
const formatDateTime = (timestamp) => {
  const date = new Date(timestamp);
  const options = {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  };
  return date.toLocaleDateString('en-US', options).replace(',', ' at');
};

// Creative Detail Modal Component
const CreativeDetailModal = ({ creativeId, onClose }) => {
  const [creative, setCreative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(null);

  // Edit/Improvement state
  const [showImprovementModal, setShowImprovementModal] = useState(false);
  const [selectedImageForEdit, setSelectedImageForEdit] = useState(null);
  const [analyzingText, setAnalyzingText] = useState(false);
  const [textMetadata, setTextMetadata] = useState(null);
  const [analysisError, setAnalysisError] = useState(null);
  const [textEdits, setTextEdits] = useState({});
  const [freeformInstructions, setFreeformInstructions] = useState('');
  const [improvementLoading, setImprovementLoading] = useState(false);
  const [improvementError, setImprovementError] = useState(null);
  const [improvementSuccess, setImprovementSuccess] = useState(false);

  // Handler to open image in new tab
  const handleViewImage = (imageUrl) => {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  };

  // Handler to download cross-origin images using backend proxy
  const handleDownloadImage = (imageUrl, filename) => {
    try {
      // Use backend proxy endpoint to download the image
      const downloadUrl = `${API_BASE_URL}/download-image?url=${encodeURIComponent(imageUrl)}&filename=${encodeURIComponent(filename || 'image.jpg')}`;

      // Create a temporary link and trigger download
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = filename || 'image.jpg';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (error) {
      console.error('Download failed:', error);
      // Fallback: open in new tab
      window.open(imageUrl, '_blank');
    }
  };

  const fetchCreativeDetails = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/creative-history/${creativeId}`);
      const data = await response.json();

      if (data.success) {
        setCreative(data);
        // Set first format as default
        const firstFormat = Object.keys(data.images)[0];
        setSelectedFormat(firstFormat);
      } else {
        setError('Failed to load creative details');
      }
    } catch (err) {
      setError('Error fetching creative details: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, [creativeId]);

  useEffect(() => {
    fetchCreativeDetails();
  }, [fetchCreativeDetails]);

  // Handle ESC key to close modal
  useEffect(() => {
    const handleEsc = (e) => {
      if (e.key === 'Escape') {
        if (showImprovementModal) {
          handleCloseImprovementModal();
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleEsc);
    return () => window.removeEventListener('keydown', handleEsc);
  }, [onClose, showImprovementModal]);

  const handleEditImage = async (img) => {
    setSelectedImageForEdit(img);
    setAnalyzingText(true);
    setAnalysisError(null);
    setTextMetadata(null);
    setShowImprovementModal(true);

    try {
      const response = await fetch(`${API_BASE_URL}/analyze-creative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ imageUrl: img.imageUrl })
      });

      const data = await response.json();

      if (data.success) {
        setTextMetadata({
          textElements: data.data?.textElements || data.textElements || [],
          tokenUsage: data.data?.tokenUsage || data.tokenUsage || {}
        });
      } else {
        throw new Error(data.error || 'Analysis failed');
      }
    } catch (error) {
      setAnalysisError(error.message);
      // Still allow freeform improvements
      setTextMetadata({ textElements: [], tokenUsage: {} });
    } finally {
      setAnalyzingText(false);
    }
  };

  const handleApplyEdit = async () => {
    setImprovementLoading(true);
    setImprovementError(null);

    try {
      // Build text changes array from edits
      const textChanges = [];
      if (textMetadata && textMetadata.textElements) {
        textMetadata.textElements.forEach((element, textIndex) => {
          const newText = textEdits[textIndex];
          if (newText !== undefined && newText !== element.text) {
            textChanges.push({
              originalText: element.text,
              newText: newText
            });
          }
        });
      }

      const payload = {
        originalCreative: {
          imageUrl: selectedImageForEdit.imageUrl,
          format: selectedImageForEdit.format,  // Explicitly include format
          variantNumber: selectedImageForEdit.variantNumber,
          style: selectedImageForEdit.style,
          // DON'T include width/height - backend will parse from format
        },
        textChanges: textChanges,
        freeformInstructions: freeformInstructions,
        sessionId: creative.session.id,
        brief: {
          brandName: creative.session.brandName,
          campaignGoal: creative.session.campaignGoal,
          targetAudience: creative.session.audienceType
        }
      };

      const response = await fetch(`${API_BASE_URL}/improve-creative`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (data.success) {
        setImprovementSuccess(true);
        // Refresh creative details to show new improved image
        setTimeout(() => {
          fetchCreativeDetails();
          handleCloseImprovementModal();
        }, 2000);
      } else {
        throw new Error(data.error || 'Improvement failed');
      }
    } catch (error) {
      setImprovementError(error.message);
    } finally {
      setImprovementLoading(false);
    }
  };

  const handleCloseImprovementModal = () => {
    setShowImprovementModal(false);
    setSelectedImageForEdit(null);
    setTextMetadata(null);
    setAnalysisError(null);
    setTextEdits({});
    setFreeformInstructions('');
    setImprovementError(null);
    setImprovementSuccess(false);
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Loading creative details...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="modal-error">
            <p>{error}</p>
            <button onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  if (!creative) return null;

  const { session, images } = creative;
  const formats = Object.keys(images);

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Modal Header with Close Button */}
        <div className="modal-header">
          <div className="modal-header-info">
            <h2>{session.brandName}</h2>
            <p className="modal-subtitle">
              {session.campaignGoal} • {session.creativeObjective}
            </p>
          </div>
          <button className="modal-close-button" onClick={onClose}>
            <X size={24} />
          </button>
        </div>

        {/* Modal Body */}
        <div className="modal-body">
          {/* Session Info Cards */}
          <div className="modal-session-info">
            <div className="modal-info-card">
              <h3>Brand Information</h3>
              <div className="info-grid">
                {session.websiteUrl && (
                  <div className="info-item">
                    <span className="info-label">Website:</span>
                    <a href={session.websiteUrl} target="_blank" rel="noopener noreferrer" className="info-value link">
                      {session.websiteUrl}
                    </a>
                  </div>
                )}
                {session.industryCategory && (
                  <div className="info-item">
                    <span className="info-label">Industry:</span>
                    <span className="info-value">{session.industryCategory}</span>
                  </div>
                )}
                {session.country && (
                  <div className="info-item">
                    <span className="info-label">Country:</span>
                    <span className="info-value">{session.country}</span>
                  </div>
                )}
              </div>
            </div>

            <div className="modal-info-card">
              <h3>Campaign Details</h3>
              <div className="info-grid">
                <div className="info-item">
                  <span className="info-label">Goal:</span>
                  <span className="info-value">{session.campaignGoal}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Objective:</span>
                  <span className="info-value">{session.creativeObjective}</span>
                </div>
                <div className="info-item">
                  <span className="info-label">Created:</span>
                  <span className="info-value">{session.relativeTime}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Format Selector */}
          <div className="modal-format-selector">
            <h3>Select Format</h3>
            <div className="format-tabs">
              {formats.map(format => (
                <button
                  key={format}
                  className={`format-tab ${selectedFormat === format ? 'active' : ''}`}
                  onClick={() => setSelectedFormat(format)}
                >
                  {format}
                  <span className="count">({images[format].length})</span>
                </button>
              ))}
            </div>
          </div>

          {/* Image Gallery */}
          <div className="modal-image-gallery">
            {selectedFormat && images[selectedFormat].map((img, index) => (
              <div key={index} className="modal-image-card">
                <div className="modal-image-header">
                  <h4>{img.format}</h4>
                  <div className="image-actions">
                    <button
                      onClick={() => handleEditImage(img)}
                      className="action-button"
                      title="Edit/Improve"
                      disabled={analyzingText || improvementLoading}
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      onClick={() => handleViewImage(img.imageUrl)}
                      className="action-button"
                      title="View in new tab"
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      onClick={() => handleDownloadImage(img.imageUrl, `${session.brandName}-${img.format}-variant${img.variantNumber}.jpg`)}
                      className="action-button"
                      title="Download"
                    >
                      <Download size={16} />
                    </button>
                  </div>
                </div>
                <div className="modal-image-wrapper">
                  <img src={img.imageUrl} alt={`${session.brandName} - ${img.format}`} />
                </div>
                <div className="modal-image-info">
                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">Variant {img.variantNumber}</span>
                  <span className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-600 ring-1 ring-inset ring-gray-500/10">{img.width} × {img.height}</span>
                  {img.fileSize && <span>{(img.fileSize / 1024).toFixed(2)} KB</span>}
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Nested Improvement Modal */}
        {showImprovementModal && (
          <div className="modal-overlay nested-improvement-modal" onClick={handleCloseImprovementModal}>
            <div className="improvement-modal" onClick={(e) => e.stopPropagation()}>
              <div className="improvement-modal-header">
                <h3>✏️ Edit & Improve Image</h3>
                <button onClick={handleCloseImprovementModal} className="modal-close-btn" disabled={improvementLoading}>×</button>
              </div>

              <div className="improvement-modal-body">
                {/* Original Creative Preview */}
                {selectedImageForEdit && (
                  <div className="original-creative-preview">
                    <h4>Original Image:</h4>
                    <img src={selectedImageForEdit.imageUrl} alt="Original" />
                    <p className="preview-info">{selectedImageForEdit.format} - Variant {selectedImageForEdit.variantNumber}</p>
                  </div>
                )}

                {/* Analysis Loading */}
                {analyzingText && (
                  <div className="analyzing-state">
                    <div className="spinner-small"></div>
                    <p>Analyzing image for text...</p>
                  </div>
                )}

                {/* Analysis Error */}
                {analysisError && (
                  <div className="analysis-error">
                    ⚠️ {analysisError}
                    <p className="error-hint">You can still use freeform instructions below.</p>
                  </div>
                )}

                {/* Text Editors */}
                {!analyzingText && textMetadata && textMetadata.textElements && textMetadata.textElements.length > 0 && (
                  <div className="text-editors-section">
                    <h4>Edit Text Elements:</h4>
                    {textMetadata.textElements.map((element, index) => (
                      <div key={index} className="text-editor-row">
                        <div className="text-editor-labels">
                          <span className="text-position-badge">{element.position}</span>
                          <span className="text-type-badge">{element.type}</span>
                          <span className="text-size-badge">{element.size}</span>
                        </div>
                        <input
                          type="text"
                          value={textEdits[index] !== undefined ? textEdits[index] : element.text}
                          onChange={(e) => setTextEdits(prev => ({ ...prev, [index]: e.target.value }))}
                          placeholder={element.text}
                          className="text-editor-input"
                          disabled={improvementLoading}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* No text found */}
                {!analyzingText && textMetadata && textMetadata.textElements.length === 0 && (
                  <div className="no-text-found">
                    ℹ️ No text detected automatically. Use freeform instructions below to describe your changes.
                  </div>
                )}

                {/* Freeform Instructions */}
                {!analyzingText && textMetadata && (
                  <div className="freeform-section">
                    <h4>Additional Instructions (Optional):</h4>
                    <textarea
                      value={freeformInstructions}
                      onChange={(e) => setFreeformInstructions(e.target.value)}
                      placeholder="e.g., change background to blue, make logo bigger, remove watermark..."
                      className="freeform-textarea"
                      rows="4"
                      disabled={improvementLoading}
                    />
                    <p className="freeform-hint">Describe any changes: remove elements, change colors, adjust positioning, etc.</p>
                  </div>
                )}

                {/* Error */}
                {improvementError && (
                  <div className="improvement-error">❌ {improvementError}</div>
                )}

                {/* Success */}
                {improvementSuccess && (
                  <div className="improvement-success">
                    ✅ Improvement complete! Refreshing...
                  </div>
                )}
              </div>

              <div className="improvement-modal-footer">
                <button onClick={handleCloseImprovementModal} className="btn-secondary" disabled={improvementLoading}>
                  Cancel
                </button>
                <button onClick={handleApplyEdit} className="btn-primary" disabled={improvementLoading || improvementSuccess || analyzingText}>
                  {improvementLoading ? (
                    <>
                      <span className="spinner-small"></span>
                      Improving...
                    </>
                  ) : (
                    '🎨 Apply Improvements'
                  )}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreativeHistory;
